import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

// Friendly labels for known top-level route segments
const SEGMENT_LABELS = {
  webinars: 'Webinars',
  industries: 'Industries',
  seminars: 'Seminars',
  speakers: 'Speakers',
  articles: 'Articles',
  bundles: 'Bundles',
  about: 'About',
  contact: 'Contact',
};

const toTitle = (seg) =>
  seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Breadcrumb — MailCraft-style, auto-generated from the current URL.
 * Renders: Home > Segment > ... > Current (very small text, no back arrow).
 * @param {string} lastLabel - Optional override for the final crumb (e.g. real page title)
 * @param {string} className - Extra classes (e.g. spacing overrides)
 */
export const Breadcrumb = ({ lastLabel, className = '' }) => {
  const { pathname } = useLocation();
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const crumbs = [{ label: 'Home', to: '/', isLast: false }];
  let path = '';
  segments.forEach((seg, i) => {
    path += `/${seg}`;
    const isLast = i === segments.length - 1;
    const label = isLast && lastLabel ? lastLabel : SEGMENT_LABELS[seg] || toTitle(seg);
    crumbs.push({ label, to: path, isLast });
  });

  return (
    <nav
      className={`flex items-center flex-wrap gap-1.5 text-[11px] mb-4 ${className}`}
      aria-label="Breadcrumb"
      data-testid="breadcrumb"
    >
      {crumbs.map((c, i) => (
        <React.Fragment key={c.to}>
          {i > 0 && <ChevronRight className="w-3 h-3 text-zinc-400 shrink-0" />}
          {c.isLast ? (
            <span className="text-zinc-900 font-medium truncate max-w-[280px]" data-testid="breadcrumb-current">
              {c.label}
            </span>
          ) : (
            <Link to={c.to} className="text-zinc-500 hover:text-zinc-900 transition-colors">
              {c.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumb;
